import { useState, useCallback, useRef, useEffect } from 'react';
import { MAPY_API_CONFIG, getApiKey, SuggestItem, SuggestResponse } from '../config/mapyApi';
import { emitApiLimitExceeded } from '../context/ApiLimitContext';

interface UseSuggestOptions {
    /** Omezení na lokalitu (default: 'CZ') */
    locality?: string;
    /** Maximální počet výsledků (default: 5) */
    limit?: number;
    /** Debounce v ms (default: 300) */
    debounceMs?: number;
    /** Typy entit k vyhledání (default: ['regional', 'poi']) */
    types?: string[];
}

interface UseSuggestReturn {
    /** Aktuální návrhy */
    suggestions: SuggestItem[];
    /** Probíhá načítání */
    loading: boolean;
    /** Chyba při načítání */
    error: Error | null;
    /** Funkce pro spuštění vyhledávání */
    search: (query: string) => void;
    /** Vyčistit návrhy */
    clear: () => void;
}

/**
 * Hook pro práci s Mapy.com Suggest API
 *
 * Nahrazuje staré SMap.Suggest
 *
 * @example
 * const { suggestions, loading, search, clear } = useSuggest();
 *
 * <input onChange={(e) => search(e.target.value)} />
 * {suggestions.map(item => <div key={item.name}>{item.name}</div>)}
 */
export const useSuggest = (options: UseSuggestOptions = {}): UseSuggestReturn => {
    const {
        locality = 'CZ',
        limit = 5,
        debounceMs = 300,
        types = ['regional', 'poi'],
    } = options;

    const [suggestions, setSuggestions] = useState<SuggestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Cache pro odpovědi
    const cacheRef = useRef<Map<string, SuggestItem[]>>(new Map());

    // Timeout ref pro debounce
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // AbortController pro zrušení předchozích requestů
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchSuggestions = useCallback(async (query: string) => {
        // Zrušit předchozí request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Kontrola minimální délky
        if (!query || query.length < 2) {
            setSuggestions([]);
            setLoading(false);
            return;
        }

        // Kontrola cache
        const cacheKey = `${query}-${locality}-${limit}`;
        if (cacheRef.current.has(cacheKey)) {
            setSuggestions(cacheRef.current.get(cacheKey)!);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        // Nový AbortController
        abortControllerRef.current = new AbortController();

        try {
            const params = new URLSearchParams({
                apikey: getApiKey(),
                query,
                limit: String(limit),
                lang: MAPY_API_CONFIG.suggest.lang,
            });

            // Přidat typy entit
            types.forEach(type => {
                params.append('type', type);
            });

            // Přidat lokalitu pro omezení na ČR
            if (locality) {
                params.append('locality', locality);
            }

            const response = await fetch(
                `${MAPY_API_CONFIG.suggest.baseUrl}?${params.toString()}`,
                { signal: abortControllerRef.current.signal }
            );

            if (!response.ok) {
                if (response.status === 403) {
                    emitApiLimitExceeded();
                }
                throw new Error(`Suggest API error: ${response.status} ${response.statusText}`);
            }

            const data: SuggestResponse = await response.json();
            const items = data.items || [];

            // Uložit do cache
            cacheRef.current.set(cacheKey, items);

            // Omezit velikost cache (max 100 položek)
            if (cacheRef.current.size > 100) {
                const firstKey = cacheRef.current.keys().next().value;
                if (firstKey) {
                    cacheRef.current.delete(firstKey);
                }
            }

            setSuggestions(items);
        } catch (err) {
            // Ignorovat AbortError
            if ((err as Error).name === 'AbortError') {
                return;
            }
            setError(err as Error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [locality, limit, types]);

    const search = useCallback((query: string) => {
        // Zrušit předchozí timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Nastavit nový timeout pro debounce
        timeoutRef.current = setTimeout(() => {
            fetchSuggestions(query);
        }, debounceMs);
    }, [fetchSuggestions, debounceMs]);

    const clear = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setSuggestions([]);
        setError(null);
        setLoading(false);
    }, []);

    // Cleanup při unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        suggestions,
        loading,
        error,
        search,
        clear,
    };
};

export default useSuggest;
